import { ReactElement, ReactNode } from "react";
import { FormInstance, FormProps } from "antd";
import { Store } from "antd/lib/form/interface";
import moment from "moment";
import { IButtonProps } from "@atoms/ZButton/interface";

export interface ICustomFormItemProps {
	value: any;
	onChange(value: any): void;
	className?: string;
	datePattern?: string;
}

export enum EWidgets {
	INPUT = "zinput",
	INPUTPASSWORD = "zpassword",
	INPUTSEARCH = "zsearch",
	INPUTTEXTAREA = "textarea",
	INPUTNUMBER = "zinputnumber",
	INPUTNUMBERWITHSELECT = "zinputnumberwithselect",
	SELECT = "zselect",
	DATEPICKER = "z-date-picker",
	TIMEPICKER = "z-time-picker",
	RADIO = "radio",
	CHECKBOX = "zcheckbox",
	CHECKBOXGROUP = "zcheckboxgroup",
	SWITCH = "switch",
	RADIOGROUP = "zradiogroup",
	RATE = "rate"
}

export interface IFormFieldProps {
	key: string;
	colSpan?: number; // if form has multiple colomns use this to say how many colSpans this field should take
	label?: string | ReactElement;
	forwardRef?: boolean; // to forward ref to widgets
	colon?: boolean;
	labelCol?: unknown; // custom labelSpan that overwrites formItemLayout given to a section
	wrapperCol?: unknown; // custom wrapperSpan that overwrites formItemLayout given to a section
	extra?: string | ReactElement; // add some extra text below input field
	dependencies?: string[];
	widget?:
		| EWidgets
		| ((formItem: ICustomFormItemProps) => ReactElement | null); // custom widget can be written if you need specific structure or above available EWidgets can be used
	render?(): void; // use this if you want to render some static text between fields of section or custom form items - ZAddTask is a good example
	widgetProps?: Record<string, any>; // Props for widget - can be anything based on widget
	onChange?(event: unknown): void;
	disabled?: boolean;
	required?: boolean;
	tooltip?: string;
	className?: string;
	initialValue?:
		| string
		| number
		| boolean
		| Array<string>
		| moment.Moment
		| Record<string, unknown>;
	message?: string; // error message to show in case of validation error
	hasFeedback?: boolean;
	validateStatus?: "success" | "warning" | "error" | "validating";
	rules?: Array<Record<string, any>>;
	noStyle?: boolean;
	children?: ReactNode;
	normalize?: (value: any, prevValue: any, allValues: any) => any; // use this if you want to manipulate text while typing, e.g., capitalize text while typing
	formItemProps?: any; // any other form item props to be passed which are  not in above list
	dateFormat?: string;
	formItemLayout?: Array<number>; // [labelSpan, wrapperSpan],
	getValueFromEvent?: (value: any) => any;
	help?: string;
}

export interface IFormMetaProps {
	columns?: number;
	formItemLayout?: Array<number>; // [labelSpan, warpperSpan]
	fields: Array<IFormFieldProps>;
}

export interface IFormSectionProps {
	title?: string | ReactElement;
	subtitle?: string;
	icon?: string;
	isTwoToneIcon?: boolean;
	iconPlaceholder?: boolean;
	visible?: boolean;
	meta?: IFormMetaProps; // use this when you want to render a static form
	getMeta?(): void; // use this when you have dynamic form and section's fields are supposed to change based on other form values - ZPreferences is a good example
	hideDivider?: boolean;
	isIndentFormItems?: boolean; // to indent form items in a section by adding left padding
	shouldHideSectionTopPadding?: boolean; // to remove default top padding for  a section
	shouldHideSectionBottomPadding?: boolean; // to remove default top padding for  a section
	isFooterSection?: boolean; // true when a custom section should be added to form footer
	hidePaddingAfterTitle?: boolean; // to remove padding after title
	showSubheadingTooltip?: boolean; // to show tooltip next to subheading
}

export interface ILayoutProps {
	labelCol?: Record<string, number>;
	wrapperCol?: Record<string, number>;
}

export interface IFormProps<T = any> extends FormProps<T> {
	key: string;
	form: FormInstance<T>;
	sections: Array<IFormSectionProps>;
	initialValues?: Store | undefined;
	viewMode?: boolean;
	loading?: boolean;
	disabled?: boolean;
	layout?: "horizontal" | "inline" | "vertical";
	requiredMark?: boolean;
	formLayout?: ILayoutProps;
	submitOnlyTouched?: boolean; // if true submits only field values that are changed by the user
	isSubmitAllowed?: boolean; // to show or hide submit button
	shouldDisableBeforeTouch?: boolean; // to disable submit button till some value in form is changed by the user
	isSubmitDisabled?: boolean; // to disable submit button
	submitButtonText?: string;
	submitButtonProps?: Record<string, any>;
	isSubmitButtonDanger?: boolean; // used for delete/disable operations
	isCancelAllowed?: boolean;
	cancelButtonText?: string;
	isStickFooter?: boolean; // to stick footer to the bottom of parent div
	isRightFooter?: boolean;
	isDynamic?: boolean; // this should be true if you want to change fields based on other field values
	onCancel?(): void;
	onFinish?(values: T): void;
	preserve?: boolean;
	formFooter?: ReactElement; // Custom form footer
	className?: string;
	shouldResetFormFields?: boolean;
	onValuesChange?: (changedValues: any, values: any) => void;
	shouldResetFormOnCancel?: boolean;
	cancelButtonProps?: IButtonProps;
	sectionColumns?: number;
}

export interface IErrorField {
	name: string;
	errors: string[];
}

interface IErrorFields {
	name: Array<string>;
	errors: Array<string>;
}

export interface IErrorInfo {
	values: Record<string, string>;
	errorFields: Array<IErrorFields>;
	outOfDate: boolean;
}

export enum EValidateStatus {
	SUCCESS = "success",
	WARNING = "warning",
	ERROR = "error",
	VALIDATING = "validating"
}
